import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useCareerActivation } from "./useCareerActivation";
import { useWallet } from "./useWallet";
import {
  buildWeeklyPaycheck,
  isoWeekKey,
  nextPayday,
  paycheckReason,
  type WeeklyPaycheckBreakdown,
} from "@/lib/payroll/weeklyPayroll";
import {
  isCareerPaycheckEligible,
  type CareerLockReason,
} from "@/lib/payroll/careerEligibility";

export interface WeeklyPaycheckResult extends WeeklyPaycheckBreakdown {
  weekKey: string;
  careerKey: string | null;
  paidAt: string;
}

/**
 * Weekly Sim Wallet paycheck.
 *  - Reads career profile from learner_career_state (annual_income).
 *  - claim() is idempotent per ISO week via a wallet_transactions reason lookup.
 */
export function useWeeklyPaycheck() {
  const { user } = useAuth();
  const { state: career, loading: careerLoading } = useCareerActivation();
  const { addFunds } = useWallet();

  const [claimedThisWeek, setClaimedThisWeek] = useState<boolean | null>(null);
  const [claiming, setClaiming] = useState(false);

  const weekKey = useMemo(() => isoWeekKey(), []);

  // Single source-of-truth gate. Nothing downstream computes pay without this.
  const eligibility = useMemo(() => isCareerPaycheckEligible(career), [career]);
  const eligible = eligibility.eligible;
  const lockReason: CareerLockReason | null = eligibility.reason;

  const breakdown = useMemo<WeeklyPaycheckBreakdown | null>(() => {
    if (!eligible || !career) return null;
    return buildWeeklyPaycheck(career.annual_income);
  }, [eligible, career]);

  const refreshClaimed = useCallback(async () => {
    if (!user || !eligible) {
      // Not eligible → not loading, definitively unclaimed. The lock UI takes over.
      setClaimedThisWeek(false);
      return;
    }
    const reason = paycheckReason(weekKey);
    const { data } = await supabase
      .from("wallet_transactions")
      .select("id")
      .eq("user_id", user.id)
      .eq("reason", reason)
      .limit(1);
    setClaimedThisWeek(Array.isArray(data) && data.length > 0);
  }, [user, weekKey, eligible]);

  useEffect(() => {
    void refreshClaimed();
  }, [refreshClaimed]);

  const claim = useCallback(async (): Promise<WeeklyPaycheckResult | null> => {
    // Hard gate: never deposit, never write a transaction, never fire a modal
    // unless the learner has earned a career through the approved module.
    if (!user || !eligible || !career || !breakdown) return null;
    if (claimedThisWeek) return null;
    setClaiming(true);
    try {
      // Re-check server-side right before deposit to avoid races / reload dupes.
      const reason = paycheckReason(weekKey);
      const { data: existing } = await supabase
        .from("wallet_transactions")
        .select("id")
        .eq("user_id", user.id)
        .eq("reason", reason)
        .limit(1);
      if (Array.isArray(existing) && existing.length > 0) {
        setClaimedThisWeek(true);
        return null;
      }
      await addFunds(breakdown.netPay, reason, undefined, "career_paycheck");
      setClaimedThisWeek(true);
      return {
        ...breakdown,
        weekKey,
        careerKey: career.career_key,
        paidAt: new Date().toISOString(),
      };
    } finally {
      setClaiming(false);
    }
  }, [user, eligible, career, breakdown, claimedThisWeek, weekKey, addFunds]);

  return {
    loading: careerLoading || claimedThisWeek === null,
    eligible,
    lockReason,
    /** @deprecated Use `eligible` — true only after a real career selection. */
    hasCareer: eligible,
    careerKey: eligible ? career?.career_key ?? null : null,
    annualSalary: eligible ? career?.annual_income ?? 0 : 0,
    breakdown,
    weekKey,
    nextPayday: useMemo(() => nextPayday(), []),
    claimedThisWeek: eligible ? !!claimedThisWeek : false,
    claiming,
    claim,
    refresh: refreshClaimed,
  };
}